import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import { useRouter } from "next/router";
import { DROP_SVG } from "../../../../constants/svgConstants";
import { useTranslation } from "next-i18next";
import { useToggle } from "../../../../hooks";
import { useOnClickOutside } from "../../../../hooks/useOnClickOutside";
import { i18n } from "../../../../../next-i18next.config";
import { useLocalStorage } from "../../../../hooks/";
import { GET_CITIES_SLUG } from "../../../../graphql/queries/queriesCity";
import { GET_CLUB_HEADER_DATA } from "../../../../graphql/queries/queriesClub";
import { UPDATE_PROFILE } from "../../../../graphql/mutations/mutationClub";
import { useAuth } from "../../../../utils/auth";
import { DEFAULT_LOCALE } from "../../../../constants/APIKeys";
import { useMutation, useQuery } from "@apollo/client";

const LANGUAGES = [
  { slug: "ru", title: "РУС" },
  { slug: "uk", title: "УКР" },
  { slug: "en", title: "ENG" },
  { slug: "kz", title: "ҚАЗ" },
  { slug: "ka", title: "GEO" },
  { slug: "pl", title: "POL" },
  { slug: "es", title: "ESP" },
  { slug: "de", title: "DEU" },
  { slug: "tr", title: "TUR" },
];

const NavBarLanguage = memo(({ setShowTabletMenu = () => null }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const { locales } = i18n;
  const router = useRouter();
  const { pathname, query, asPath } = router;
  const { country, club } = query;
  const { isAuthorized } = useAuth();

  const { data: cityData } = useQuery(GET_CITIES_SLUG, {
    variables: { slug: country, language },
    skip: !country,
  });
  const { data: clubData } = useQuery(GET_CLUB_HEADER_DATA, {
    variables: { showOffer: false },
    skip: !club,
  });

  // Определяем доступные языки на основе контекста (город > клуб > все сконфигурированные локали)
  const languagesFromContext = useMemo(() => {
    if (cityData?.country?.languages?.length > 0) {
      return cityData.country.languages;
    }
    if (clubData?.club?.languages?.length > 0) {
      return clubData.club.languages;
    }
    return locales;
  }, [cityData, clubData, locales]);

  // Определяем язык по умолчанию на основе контекста (город > клуб)
  const languageDefaultFromContext = useMemo(() => {
    return cityData?.country?.languageDefault ?? clubData?.club?.languageDefault;
  }, [cityData, clubData]);

  // Фильтруем список всех языков (LANGUAGES) по тем, которые доступны в текущем контексте
  const ALL_LANGUAGES = useMemo(() => {
    const availableSlugsInContext = new Set(languagesFromContext);
    return LANGUAGES.filter((lang) => availableSlugsInContext.has(lang.slug));
  }, [languagesFromContext]); // Зависимость от LANGUAGES (константа) не обязательна, но безопасна

  // Создаем массив доступных slug-ов для удобства проверок
  const availableSlugs = useMemo(() => ALL_LANGUAGES.map((lang) => lang.slug), [ALL_LANGUAGES]);
  // Определяем резервный язык (fallback), который будет использоваться, если в localStorage нет данных или они невалидны.
  // Приоритет: Язык по умолчанию из GraphQL > Текущая локаль роутера > Язык по умолчанию i18n > Первый доступный
  const fallbackLanguage = useMemo(() => {
    // 1. Проверяем язык по умолчанию из GraphQL (если он есть и доступен)
    if (languageDefaultFromContext && availableSlugs.includes(languageDefaultFromContext)) {
      return languageDefaultFromContext;
    }
    // 2. Проверяем текущую локаль роутера (если она доступна)
    // Это важно для случаев, когда пользователь зашел на URL с определенной локалью (например, /en/...)
    // или после успешной навигации router.locale уже обновился.
    if (language && availableSlugs.includes(language)) {
      return language;
    }
    // 3. Резервный вариант: язык по умолчанию из конфига next-i18next (если доступен)
    if (i18n.defaultLocale && availableSlugs.includes(i18n.defaultLocale)) {
      return i18n.defaultLocale;
    }
    // 4. Последний резервный вариант: первый доступный язык в списке
    return availableSlugs.at(0) || DEFAULT_LOCALE; // Возвращаем 'ru' как крайний резерв, если список пуст
  }, [availableSlugs, languageDefaultFromContext, language, i18n.defaultLocale]);
  // Используем useLocalStorage для хранения ПРЕДПОЧТЕНИЯ пользователя.
  // Он инициализируется значением из ключа "_language" в хранилище,
  // или fallbackLanguage, если в хранилище нет данных.
  // Тип может быть string или undefined, если хранилище пусто или initialValue такой.
  const [storedLanguagePreference, setStoredLanguagePreference] = useLocalStorage(fallbackLanguage, "_language");
  // Мутация для обновления языка профиля на бэкенде
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  // Эффект для синхронизации локали роутера и обновления профиля на основе storedLanguagePreference.
  // Этот эффект срабатывает при изменении storedLanguagePreference (клик пользователя)
  // или при изменении relevant context (например, languageDefault или available languages из GraphQL)
  // или при первоначальном монтировании, если сохраненное предпочтение не совпадает с локалью роутера.
  useEffect(() => {
    // Определяем целевой язык, на который должен быть установлен роутер.
    // Используем сохраненное предпочтение, если оно есть и является доступным языком.
    // В противном случае используем рассчитанный fallbackLanguage.
    const targetLanguageToSync =
      storedLanguagePreference && availableSlugs.includes(storedLanguagePreference) ? storedLanguagePreference : fallbackLanguage;
    // Избегаем push, если целевой язык не определен или уже является текущей локалью роутера
    if (targetLanguageToSync && router.locale !== targetLanguageToSync) {
      // Дополнительная проверка: является ли целевой язык одной из сконфигурированных локалей Next.js
      // По идее, он должен быть, т.к. derived из availableSlugs, которые сами основаны на locales или GraphQL.
      if (locales.includes(targetLanguageToSync)) {
        // console.log(`[NavBarLanguage useEffect] Syncing router locale to: ${targetLanguageToSync}`); // Отладочный вывод
        // Обновляем профиль пользователя на бэкенде, если пользователь авторизован
        // Делаем это ДО навигации, чтобы бэкенд знал о смене языка перед загрузкой новой страницы
        if (isAuthorized) {
          updateProfile({
            variables: { language: targetLanguageToSync }, // Используем целевой язык для синхронизации
            onError: (err) => console.error("Failed to update profile language:", err), // Логируем ошибки мутации
          });
        }
        // Выполняем навигацию для изменения локали роутера
        // Можно использовать shallow routing, если изменение локали не требует полной перезагрузки страницы
        // router.push({ pathname, query }, asPath, { locale: targetLanguageToSync, shallow: true }); // requires careful testing with your app setup
        router.push({ pathname, query }, asPath, { locale: targetLanguageToSync });
        // Вызываем функцию закрытия мобильного меню или другие необходимые действия после навигации
        setShowTabletMenu();
      } else {
        console.warn(
          `[NavBarLanguage useEffect] Target language "${targetLanguageToSync}" is not in N configured locales. Cannot navigate.`
        );
        // Этот случай указывает на проблему с логикой определения доступных языков или конфигом Next.js.
      }
    }
    // Зависимости: storedLanguagePreference (изменяется при клике),
    // fallbackLanguage (может измениться при смене страницы/данных GraphQL),
    // router.locale (изменяется после успешного router.push),
    // availableSlugs (могут измениться при смене контекста),
    // isAuthorized, updateProfile, router, pathname, query, asPath, setShowTabletMenu, locales.
    // Включение 'router' в зависимости может вызвать бесконечный цикл, если логика внутри эффекта не предотвращает его.
    // Здесь цикл предотвращается проверкой router.locale !== targetLanguageToSync.
  }, [
    storedLanguagePreference,
    fallbackLanguage,
    router.locale,
    availableSlugs,
    isAuthorized,
    updateProfile,
    router,
    pathname,
    query,
    asPath,
    setShowTabletMenu,
    locales,
  ]);
  // Логика переключения выпадающего меню
  const [showDropMenu, toggleDropMenu, hideDropMenu] = useToggle();
  // Реф для элемента списка, чтобы отслеживать клики вне его
  const ref = useRef(null); // Указываем тип ref
  useOnClickOutside(ref, hideDropMenu); // Хук для скрытия меню при клике вне
  // Функция смены языка, вызываемая при клике на пункт выпадающего списка
  const changeLanguage = useCallback(
    (lang) => {
      // Проверяем, что выбранный язык доступен
      if (availableSlugs.includes(lang)) {
        // Устанавливаем предпочтение пользователя в localStorage.
        // useEffect выше среагирует на это изменение и синхронизирует роутер.
        setStoredLanguagePreference(lang);
        hideDropMenu(); // Закрываем выпадающее меню
      } else {
        console.warn(`Attempted to set unavailable language: ${lang}`);
      }
    },
    [setStoredLanguagePreference, availableSlugs, hideDropMenu] // Зависимости
  );
  // Определяем заголовок для отображения (основываясь на текущей локали роутера)
  const pickedLanguageTitle = useMemo(() => {
    const currentLangData = ALL_LANGUAGES.find(({ slug }) => slug === language); // Используем router.locale (language из useTranslation)
    // Резервные варианты заголовка, если текущий язык не найден в ALL_LANGUAGES
    return (
      currentLangData?.title ||
      ALL_LANGUAGES.find(({ slug }) => slug === i18n.defaultLocale)?.title ||
      ALL_LANGUAGES.at(0)?.title ||
      language
    );
  }, [ALL_LANGUAGES, language, i18n.defaultLocale]);
  // Определяем языки для отображения в выпадающем списке (все доступные, кроме текущего)
  const languagesForDropdown = useMemo(() => {
    return ALL_LANGUAGES.filter(({ slug }) => slug !== language); // Фильтруем текущую локаль роутера
  }, [ALL_LANGUAGES, language]);

  // Определяем, нужно ли показывать выпадающее меню (если доступно более одного языка и более одной локали в конфиге)
  const showDropdown = ALL_LANGUAGES.length > 1 && locales.length > 1;

  return (
    <li className={`navbar-search-item dropdown-language-button`} onClick={(e) => e.stopPropagation()} ref={ref}>
      {showDropdown ? (
        <div
          className={`dropdown-button dropdown-button__language ${showDropMenu ? "dropdown-button_opened" : ""}`}
          onClick={toggleDropMenu}
          aria-haspopup="true"
          aria-expanded={showDropMenu}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleDropMenu();
            }
          }}
        >
          {pickedLanguageTitle}
          <span className="dropdown-button__drop-svg">{DROP_SVG}</span>
        </div>
      ) : (
        // Если доступен только один язык или локалей <= 1, просто показываем заголовок языка
        <div className={`dropdown-button dropdown-button__language`}>{pickedLanguageTitle}</div>
      )}
      {/* Показываем выпадающее меню только если showDropMenu true и showDropdown true */}
      {showDropMenu && showDropdown && (
        <div className="dropdown-language-menu" aria-label="dropdown menu for language selection" role="menu">
          {languagesForDropdown.map(({ title, slug }) => (
            <span
              key={slug}
              className="dropdown-language-item"
              onClick={() => changeLanguage(slug)}
              role="menuitem" // ARIA-атрибут для пунктов меню
              tabIndex={-1} // Делаем пункты меню фокусируемыми, но не в стандартном потоке
              onKeyDown={(e) => {
                // Обработка нажатий клавиатуры на пунктах меню
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  changeLanguage(slug);
                }
              }}
            >
              {title}
            </span>
          ))}
        </div>
      )}
    </li>
  );
});
export default NavBarLanguage;
